import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { LEGAL_PAGES } from "../data/legal";
import { ArrowLeft, FileText, Calendar, AlertTriangle, Download } from "lucide-react";
import useSEO from "../lib/useSEO";
import { API } from "../lib/api";

export default function Legal() {
  const { slug } = useParams();
  const nav = useNavigate();
  const data = LEGAL_PAGES[slug];

  useSEO({
    title: data?.title || "Mentions légales",
    description: data
      ? `${data.title} d'EventMarket Pro. ${data.eyebrow}.`
      : "Informations légales d'EventMarket Pro.",
  });

  useEffect(() => {
    if (!data) nav("/", { replace: true });
    else window.scrollTo({ top: 0 });
  }, [slug, data, nav]);

  if (!data) return null;

  return (
    <div className="bg-cream-50 min-h-[calc(100vh-80px)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <Link
          to="/"
          data-testid="legal-back"
          className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-ink mb-10"
        >
          <ArrowLeft className="h-3 w-3" /> Retour à l&apos;accueil
        </Link>

        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-5 w-5 text-accent" />
          <p className="text-label text-muted-foreground">{data.eyebrow}</p>
        </div>
        <h1
          data-testid={`legal-title-${data.slug}`}
          className="font-heading font-light tracking-editorial text-4xl lg:text-5xl text-ink mb-4 leading-tight"
        >
          {data.title}
        </h1>
        <p className="flex items-center gap-2 text-xs text-muted-foreground mb-12">
          <Calendar className="h-3 w-3" /> Dernière mise à jour : {data.updated}
        </p>

        {slug === "cgv" && (
          <a
            href={`${API}/docs/complaints-procedure.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-complaints-pdf"
            className="inline-flex items-center gap-2 border border-ink/15 hover:border-accent hover:text-accent transition-colors bg-white/40 backdrop-blur-sm text-ink text-sm px-4 py-3 mb-12 rounded-md group"
          >
            <Download className="h-4 w-4" />
            <span className="font-medium">Télécharger la procédure de réclamations</span>
            <span className="text-ink/50 group-hover:text-accent/70 text-xs">PDF · 4 Ko</span>
          </a>
        )}

        <article className="space-y-8">
          {data.sections.map((s, idx) => (
            <section key={idx} data-testid={`legal-section-${idx}`}>
              <h2 className="font-heading font-medium text-xl text-ink mb-3 tracking-tight">{s.h}</h2>
              <div
                className="text-[15px] leading-relaxed text-ink/80 [&_a]:text-accent [&_a]:underline [&_a:hover]:text-ink [&_strong]:text-ink"
                dangerouslySetInnerHTML={{ __html: s.p }}
              />
            </section>
          ))}
        </article>

        <div className="mt-16 pt-8 border-t border-ink/10">
          <div
            data-testid="legal-disclaimer"
            className="bg-accent/5 border-l-4 border-accent rounded-md p-4 flex items-start gap-3"
          >
            <AlertTriangle className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-ink mb-1">Document juridique</p>
              <p className="text-xs text-ink/65 leading-relaxed">
                Ces conditions sont rédigées par EventMarket Pro (SAVEURS NORMANDES) pour
                la marketplace eventmarketpro.fr. Pour toute question, écrivez à{" "}
                <a href="mailto:hello@eventmarketpro.fr" className="text-accent underline">
                  hello@eventmarketpro.fr
                </a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
